import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Button from '@mui/material/Button';
import { CircularProgress, TextField } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import LockIcon from '@mui/icons-material/Lock';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import SeoHelmet from '../common/SeoHelmet';
import validateEmail from '../../helpers/validateEmail';
import validateDisplayName, { normalizeDisplayName } from '../../helpers/validateDisplayName';
import { Context } from '../../App';
import Header from '../common/Header';
import Footer from '../common/Footer';
import MuiSnackbar from '../common/MuiSnackbar';
const SITE_KEY = '6LeCkOwrAAAAABtZT8OmN0dzwiX54yWeyRLN9SN7'; // public key

const Signup = () => {
  //Snackbar states
  const [open, setOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const { setLastpage, setUser, theme } = useContext(Context);

  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [checked, setChecked] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  //error states
  const [errorName, errorNameSet] = useState(false);
  const [errorEmail, errorEmailSet] = useState(false);
  const [errorPassword, errorPasswordSet] = useState(false);
  const [errorPasswordConfirm, errorPasswordConfirmSet] = useState(false);

  //error messages
  const [errorNameMessage, errorNameMessageSet] = useState('');
  const [errorEmailMessage, errorEmailMessageSet] = useState('');
  const [errorPasswordMessage, errorPasswordMessageSet] = useState('');
  const [errorPasswordConfirmMessage, errorPasswordConfirmMessageSet] = useState('');
  const nav = useNavigate();

  const captchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);

  // ---- mounted guard to prevent setState on unmounted component ----
  const mounted = useRef(true);
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);
  // -----------------------------------------------------------------

  useEffect(() => {
    // theme changed → new widget with different theme
    // ensure no stale token from the previous widget
    setCaptchaToken(null);
    // optional, in case the old instance still exists briefly
    if (captchaRef.current) captchaRef.current.reset?.();
  }, [theme]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = captchaRef.current?.getValue();

    if (!token) {
      // Reset only on missing token
      if (captchaRef.current) captchaRef.current.reset();
      setCaptchaToken(null);
      if (mounted.current) {
        setAlertMessage('Please complete the reCAPTCHA.');
        setOpen(true);
      }
      return;
    }

    try {
      const { data } = await axios.post('/api/v1/users/verify-token', {
        reCAPTCHA_TOKEN: token,
      });

      if (data?.ok === true) {
        // On success: do NOT reset; navigate immediately inside signup
        await signup();
      } else {
        // Reset only on verification failure
        if (captchaRef.current) captchaRef.current.reset();
        setCaptchaToken(null);
        if (mounted.current) {
          setAlertMessage('reCAPTCHA verification failed. Try again.');
          setOpen(true);
        }
      }
    } catch (err) {
      // Reset only on error
      if (captchaRef.current) captchaRef.current.reset();
      setCaptchaToken(null);
      if (mounted.current) {
        setAlertMessage('Error verifying reCAPTCHA. Try again.');
        setOpen(true);
      }
    }
  };

  const onCaptchaChange = (value) => {
    setCaptchaToken(value || null);
  };

  const handleClickShowPassword = (variable, setter) => {
    setter(!variable);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  //Name Validation
  const validateName = (name) => {
    if (!validateDisplayName(name)) {
      errorNameSet(true);
      errorNameMessageSet(
        'Use 2-50 characters. Letters, numbers, spaces, apostrophes, hyphens, periods, underscores, and plus signs are allowed.'
      );
      return false;
    } else {
      return true;
    }
  };

  useEffect(() => {
    if (validateDisplayName(name)) {
      errorNameMessageSet('');
      errorNameSet(false);
    }
  }, [name]);

  //Email Validation
  const validateEmailAndHandleError = (email) => {
    if (!validateEmail(email)) {
      errorEmailSet(true);
      errorEmailMessageSet('Please provide a valid email.');
      return false;
    } else {
      return true;
    }
  };

  useEffect(() => {
    if (validateEmail(email)) {
      errorEmailMessageSet('');
      errorEmailSet(false);
    }
  }, [email]);

  //Password Validation
  const validatePassword = (password) => {
    if (password.length < 8) {
      errorPasswordSet(true);
      errorPasswordMessageSet('Password must be min. 8 characters.');
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (password.length > 7) {
      errorPasswordMessageSet('');
      errorPasswordSet(false);
    }
    if (passwordConfirm === password) {
      errorPasswordMessageSet('');
      errorPasswordSet(false);
    }
  }, [password, passwordConfirm]);

  //PasswordConfirm Validation
  const validatePasswordConfirm = (password, passwordConfirm) => {
    if (passwordConfirm.length < 8) {
      errorPasswordConfirmSet(true);
      errorPasswordConfirmMessageSet('"Password Confirm" must be min. 8 characters.');
      return false;
    }
    if (password !== passwordConfirm) {
      errorPasswordConfirmSet(true);
      errorPasswordConfirmMessageSet('"Password" and "Password Confirm" must match.');
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (passwordConfirm.length > 7) {
      errorPasswordConfirmMessageSet('');
      errorPasswordConfirmSet(false);
    }
    if (passwordConfirm === password) {
      errorPasswordConfirmMessageSet('');
      errorPasswordConfirmSet(false);
    }
  }, [password, passwordConfirm]);

  //Handle disabled/enabled state of Sign Up button
  useEffect(() => {
    if (
      checked &&
      validateName(name) &&
      validateEmail(email) &&
      validatePassword(password) &&
      validatePasswordConfirm(password, passwordConfirm) &&
      !!captchaToken
    ) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, name, email, password, passwordConfirm, captchaToken]);

  const signup = async () => {
    if (mounted.current) setLoading(true);

    try {
      const response = await axios({
        method: 'POST',
        url: '/api/v1/users/signup',
        data: {
          name: normalizeDisplayName(name),
          email,
          password,
          passwordConfirm,
        },
      });

      if (response.data.status === 'success') {
        if (mounted.current) {
          setUser(response.data.data.user);
          setLastpage('./signup');
        }
        nav('/'); // unmounts this component
        return; // prevent any further setState in this function
      }
    } catch (err) {
      if (err.response?.data?.code === 'PASSWORD_RESET_REQUIRED') {
        nav(`/forgotpassword?email=${encodeURIComponent(email)}&recovery=1`, {
          state: { recoveryMessage: err.response?.data?.message || '' },
        });
        return;
      }

      if (mounted.current) {
        setAlertMessage(err.response?.data?.message || 'Could not create account.');
        setOpen(true);
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  return (
    <>
      <SeoHelmet
        title="GeoCalc | Sign Up"
        description="Create your GeoCalc account to access professional geotechnical calculation tools."
        indexable={false}
        robotsOverride="noindex, nofollow, noarchive, nosnippet, noimageindex, notranslate, max-image-preview:none, max-video-preview:0, max-snippet:0"
        schemaAuto={false}
      />

      <Header />
      <div className="signup">
        <MuiSnackbar open={open} setOpen={setOpen} severity="error" alertMessage={alertMessage} />
        <LockIcon color="primary" fontSize="large" />
        <h2>Sign Up</h2>
        <form onSubmit={handleSubmit}>
          <TextField
            onBlur={(e) => validateName(e.target.value)}
            className="test-signup-name"
            style={{ width: 300 }}
            size="small"
            label="Name"
            variant="outlined"
            autoComplete="name"
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
            error={errorName}
            helperText={errorNameMessage}
          />
          <TextField
            onBlur={(e) => validateEmailAndHandleError(e.target.value)}
            className="test-signup-email"
            style={{ width: 300 }}
            size="small"
            label="Email"
            variant="outlined"
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            error={errorEmail}
            helperText={errorEmailMessage}
          />
          <TextField
            onBlur={(e) => validatePassword(e.target.value)}
            className="test-signup-password"
            size="small"
            style={{ width: 300 }}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => {
                      handleClickShowPassword(showPassword, setShowPassword);
                    }}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            label="Password"
            disabled={loading}
            error={errorPassword}
            helperText={errorPasswordMessage}
          />
          <TextField
            onBlur={(e) => validatePasswordConfirm(password, e.target.value)}
            className="test-signup-password-confirm"
            size="small"
            style={{ width: 300 }}
            type={showPasswordConfirm ? 'text' : 'password'}
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => {
                      handleClickShowPassword(showPasswordConfirm, setShowPasswordConfirm);
                    }}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            label="Confirm Password"
            disabled={loading}
            error={errorPasswordConfirm}
            helperText={errorPasswordConfirmMessage}
          />
          <label>
            <Checkbox
              className="test-check-terms"
              size="small"
              onChange={() => setChecked(!checked)}
              checked={checked}
              disabled={loading}
            ></Checkbox>
            I've read
            <Link component={RouterLink} to="/terms-of-use">
              {' '}
              terms and conditions
            </Link>
          </label>

          {!loading && (
            <ReCAPTCHA
              key={`recaptcha-${theme}`}
              className="recaptcha"
              sitekey={SITE_KEY}
              ref={captchaRef}
              theme={theme === 'light' ? 'light' : 'dark'}
              onChange={onCaptchaChange}
            />
          )}

          <Button
            className="test-signup-button"
            variant="contained"
            style={{ width: 300 }}
            fullWidth
            type="submit"
            disabled={buttonDisabled || loading}
          >
            {loading ? <CircularProgress color="inherit" size={20} /> : 'SIGN UP'}
          </Button>
        </form>
        <Link component={RouterLink} to="/forgotpassword">
          Used GeoCalc before? Reset your password here.
        </Link>
        <br />
        <Link component={RouterLink} to="/login">
          Already have an account? Sign in here!
        </Link>
      </div>
      <Footer />
    </>
  );
};

export default Signup;
